import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { SISTEMAS_EM_ORDEM, nomeDoSistema } from "../sistemas";
import {
  IconPlus,
  IconTrash,
  IconCheck,
  IconRepeat,
  IconAlert,
  IconClock,
  IconMail,
  IconClose,
} from "../icons";
import ErrorState from "./ErrorState";
import RenovacoesView from "./RenovacoesView";
import { dataBr } from "./PainelView";

/**
 * LEMBRETES — o que não pode ser esquecido.
 *
 * Aparecem sempre nesta tela (e no Painel). O e-mail é um extra: o servidor de
 * e-mail da empresa é cadastrado AQUI mesmo (botão "Envio por e-mail"), com
 * teste de verdade. Enquanto não estiver configurado, a tela avisa — em vez de
 * fingir que mandou.
 */

const VAZIO = { titulo: "", detalhe: "", data: "", hora: "", sistema: "", email_para: "" };

export default function LembretesView({ onMudou }: { onMudou: () => void }) {
  const [linhas, setLinhas] = useState<any[]>([]);
  const [emailOk, setEmailOk] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<any | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);
  const [configAberta, setConfigAberta] = useState(false);
  /* Duas listas na MESMA aba (sem criar aba nova no menu): o que precisa ser
     lembrado agora e o que vence mais adiante. */
  const [sub, setSub] = useState<"lembretes" | "renovacoes">("lembretes");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.lembretes.listar();
      setLinhas(r.lembretes ?? []);
      setEmailOk(!!r.email?.configurado);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar os lembretes.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar() {
    if (!form?.titulo?.trim() || !form?.data) {
      alert("Escreva o lembrete e escolha o dia.");
      return;
    }
    setSalvando(true);
    try {
      if (form.id) await api.lembretes.atualizar(form.id, form);
      else await api.lembretes.criar(form);
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  async function alternar(l: any) {
    try {
      await api.lembretes.atualizar(l.id, {
        status: l.status === "Concluído" ? "Aberto" : "Concluído",
      });
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao atualizar.");
    }
  }

  async function enviar(l: any) {
    try {
      const r = await api.lembretes.enviar(l.id);
      alert(r?.mensagem ?? "E-mail enviado.");
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível enviar.");
    }
  }

  async function excluir(l: any) {
    if (!confirm("Apagar o lembrete " + l.titulo + "?")) return;
    try {
      await api.lembretes.remover(l.id);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  const set = (campo: string) => (e: any) => setForm({ ...form, [campo]: e.target.value });
  const visiveis = mostrarConcluidos ? linhas : linhas.filter((l) => l.status !== "Concluído");

  return (
    <>
      <div className="triagem-abas">
        <button className={sub === "lembretes" ? "ativa" : ""} onClick={() => setSub("lembretes")}>
          Lembretes
        </button>
        <button className={sub === "renovacoes" ? "ativa" : ""} onClick={() => setSub("renovacoes")}>
          Renovações
        </button>
      </div>

      {sub === "renovacoes" ? (
        <RenovacoesView />
      ) : (
        <>
      {!emailOk && (
        <div className="dev-alerta">
          <IconAlert size={18} />
          <span>
            O envio por <strong>e-mail</strong> está desligado. Os lembretes continuam aparecendo
            aqui e no painel — para recebê-los também por e-mail, cadastre o servidor de e-mail da
            empresa.
          </span>
          <button className="btn-primary btn-sm" onClick={() => setConfigAberta(true)}>
            Configurar envio
          </button>
        </div>
      )}

      <div className="crud-toolbar">
        <label className="dev-check">
          <input
            type="checkbox"
            checked={mostrarConcluidos}
            onChange={(e) => setMostrarConcluidos(e.target.checked)}
          />
          Mostrar concluídos
        </label>
        <div className="toolbar-right">
          <button
            className="btn-secondary"
            onClick={() => setConfigAberta(true)}
            title="Servidor de e-mail e para quem os lembretes vão"
          >
            <IconMail size={16} /> Envio por e-mail
          </button>
          <button className="btn-primary" onClick={() => setForm({ ...VAZIO })}>
            <IconPlus size={16} /> Novo lembrete
          </button>
        </div>
      </div>

      {configAberta && (
        <ConfigEmail
          onFechar={() => setConfigAberta(false)}
          onSalvou={() => {
            carregar();
            onMudou();
          }}
        />
      )}

      {form && (
        <div className="form-card">
          <div className="form-grid">
            <label className="field field-wide">
              <span>
                Lembrar de <span className="req">*</span>
              </span>
              <input value={form.titulo} onChange={set("titulo")} autoFocus />
            </label>
            <label className="field">
              <span>
                Dia <span className="req">*</span>
              </span>
              <input type="date" value={form.data ?? ""} onChange={set("data")} />
            </label>
            <label className="field">
              <span>Hora</span>
              <input type="time" value={form.hora ?? ""} onChange={set("hora")} />
            </label>
            <label className="field">
              <span>Sistema</span>
              <select value={form.sistema ?? ""} onChange={set("sistema")}>
                <option value="">Geral</option>
                {SISTEMAS_EM_ORDEM.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field-wide">
              <span>Detalhe</span>
              <textarea rows={2} value={form.detalhe ?? ""} onChange={set("detalhe")} />
            </label>
            <label className="field field-wide">
              <span>Mandar o e-mail para (opcional)</span>
              <input
                value={form.email_para ?? ""}
                onChange={set("email_para")}
                placeholder="Em branco: vai para o e-mail do responsável"
              />
            </label>
          </div>
          <div className="solicitar-foot">
            <button
              className="btn-secondary btn-sm"
              onClick={() => setForm(null)}
              disabled={salvando}
            >
              Cancelar
            </button>
            <button className="btn-primary btn-sm" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : form.id ? "Salvar" : "Criar lembrete"}
            </button>
          </div>
        </div>
      )}

      {erro ? (
        <ErrorState message={erro} onRetry={carregar} />
      ) : carregando ? (
        <div className="carregando">Carregando…</div>
      ) : visiveis.length === 0 ? (
        <div className="empty">
          <IconClock size={30} />
          Nenhum lembrete em aberto.
        </div>
      ) : (
        <ul className="dev-lista dev-lista-cards">
          {visiveis.map((l) => (
            <li key={l.id} className={l.status === "Concluído" ? "dev-passou" : ""}>
              <span
                className={
                  "pill " +
                  (l.status === "Concluído"
                    ? "pill-neutral"
                    : l.atrasado
                      ? "pill-danger"
                      : l.hoje
                        ? "pill-warn"
                        : "pill-neutral")
                }
              >
                {l.atrasado && l.status !== "Concluído" ? "atrasado " : ""}
                {dataBr(l.data)}
                {l.hora ? " " + l.hora : ""}
              </span>
              <span className="dev-lista-main">
                <strong>{l.titulo}</strong>
                <small className="muted">
                  {l.origem && l.origem !== "Manual" ? "criado pelo sistema · " + l.origem : nomeDoSistema(l.sistema)}
                  {l.urgente && l.status !== "Concluído" ? " · urgente" : ""}
                  {/* Só a primeira linha do detalhe: o resto (contato, pauta) fica no e-mail. */}
                  {l.detalhe ? " · " + String(l.detalhe).split("\n")[0] : ""}
                  {l.reuniao_titulo ? " · reunião: " + l.reuniao_titulo : ""}
                  {l.solicitacao_titulo ? " · pedido: " + l.solicitacao_titulo : ""}
                  {l.email_em ? " · e-mail enviado" : ""}
                </small>
              </span>
              <span className="dev-acoes">
                <button
                  className={"icon-btn " + (l.status === "Concluído" ? "" : "icon-btn-ok")}
                  title={l.status === "Concluído" ? "Reabrir" : "Concluir"}
                  onClick={() => alternar(l)}
                >
                  {l.status === "Concluído" ? <IconRepeat size={16} /> : <IconCheck size={16} />}
                </button>
                <button
                  className="icon-btn"
                  title={emailOk ? "Enviar por e-mail agora" : "E-mail não configurado"}
                  disabled={!emailOk}
                  onClick={() => enviar(l)}
                >
                  <IconMail size={16} />
                </button>
                <button
                  className="icon-btn icon-btn-danger"
                  title="Excluir"
                  onClick={() => excluir(l)}
                >
                  <IconTrash size={16} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
        </>
      )}
    </>
  );
}

/* ==================== ENVIO POR E-MAIL (configuração) ====================
 *
 * Os dados do servidor de e-mail da empresa ficam AQUI, na tela, e não num
 * arquivo dentro do servidor: quem usa o sistema precisa conseguir trocar a
 * senha do e-mail sozinha. A senha vai para o banco cifrada (server/email.ts) e
 * NUNCA volta para a tela — o campo em branco significa "mantenha a que está".
 *
 * O botão "Salvar e testar" manda um e-mail de verdade para o responsável: é o
 * único jeito honesto de dizer se a configuração está certa, em vez de esperar
 * o próximo lembrete para descobrir que não saiu.
 */
function ConfigEmail({ onFechar, onSalvou }: { onFechar: () => void; onSalvou: () => void }) {
  const [dados, setDados] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [senha, setSenha] = useState("");

  const carregar = useCallback(async () => {
    try {
      setDados(await api.email.config());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a configuração.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const c = dados?.config;
  const set = (campo: string) => (e: any) =>
    setDados({ ...dados, config: { ...c, [campo]: e.target.value } });

  /** Salva o que está na tela. Devolve false quando o servidor recusou. */
  async function salvar(): Promise<boolean> {
    setErro(null);
    setAviso(null);
    if (!c?.servidor?.trim() || !c?.usuario?.trim()) {
      setErro("Preencha o servidor e o usuário do e-mail.");
      return false;
    }
    if (!senha.trim() && !dados.temSenha) {
      setErro("Digite a senha do e-mail.");
      return false;
    }
    try {
      await api.email.salvar({
        ativo: c.ativo !== false,
        servidor: c.servidor,
        porta: c.porta,
        seguranca: c.seguranca,
        usuario: c.usuario,
        remetente: c.remetente,
        remetente_nome: c.remetente_nome,
        responsavel_email: c.responsavel_email,
        resumo_ativo: c.resumo_ativo !== false,
        resumo_hora: c.resumo_hora,
        urgente_ativo: c.urgente_ativo !== false,
        solicitacao_ativo: c.solicitacao_ativo !== false,
        senha: senha.trim() || undefined,
      });
      setSenha("");
      await carregar();
      onSalvou();
      return true;
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
      return false;
    }
  }

  async function salvarSo() {
    setOcupado(true);
    if (await salvar()) setAviso("Configuração salva.");
    setOcupado(false);
  }

  async function salvarETestar() {
    setOcupado(true);
    if (await salvar()) {
      try {
        const r = await api.email.testar();
        setAviso(r?.mensagem ?? "E-mail de teste enviado.");
        await carregar();
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : "Não foi possível enviar o teste.");
      }
    }
    setOcupado(false);
  }

  return (
    <div className="modal-overlay" onMouseDown={onFechar}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Envio por e-mail</h3>
            <p className="modal-sub">
              Dados do servidor de e-mail da empresa. Peça ao seu provedor de e-mail (ou a quem
              cuida dele) o endereço do servidor de saída, a porta e a senha.
            </p>
          </div>
          <button className="icon-btn" onClick={onFechar}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {!dados ? (
            <p className="muted">Carregando…</p>
          ) : (
            <>
              {dados.origem === "env" && (
                <p className="dev-email-nota">
                  Hoje o envio está usando o que foi preenchido no arquivo <code>.env</code> do
                  servidor. Ao salvar aqui, passa a valer o que está nesta tela.
                </p>
              )}
              {dados.ultimo_erro && (
                <p className="dev-email-erro">
                  <strong>A última tentativa falhou:</strong> {dados.ultimo_erro}
                </p>
              )}

              <div className="form-grid">
                <label className="field field-wide">
                  <span>
                    E-mail do responsável (recebe os lembretes) <span className="req">*</span>
                  </span>
                  <input
                    value={c.responsavel_email ?? ""}
                    onChange={set("responsavel_email")}
                    placeholder="voce@sercamp.com.br"
                    inputMode="email"
                  />
                </label>

                <label className="field field-wide">
                  <span>
                    Servidor de saída <span className="req">*</span>
                  </span>
                  <input
                    value={c.servidor ?? ""}
                    onChange={set("servidor")}
                    placeholder="smtp.suaempresa.com.br"
                    autoFocus
                  />
                </label>
                <label className="field">
                  <span>Porta</span>
                  <input type="number" value={c.porta ?? 587} onChange={set("porta")} />
                </label>
                <label className="field">
                  <span>Segurança</span>
                  <select value={c.seguranca ?? "starttls"} onChange={set("seguranca")}>
                    <option value="starttls">STARTTLS (recomendado — costuma ser a porta 587)</option>
                    <option value="ssl">SSL/TLS (costuma ser a porta 465)</option>
                    <option value="nenhuma">Sem criptografia</option>
                  </select>
                </label>

                <label className="field">
                  <span>
                    Usuário <span className="req">*</span>
                  </span>
                  <input
                    value={c.usuario ?? ""}
                    onChange={set("usuario")}
                    placeholder="normalmente o e-mail inteiro"
                  />
                </label>
                <label className="field">
                  <span>
                    Senha {!dados.temSenha && <span className="req">*</span>}
                  </span>
                  <input
                    type="password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder={dados.temSenha ? "•••••••• (guardada)" : ""}
                    autoComplete="new-password"
                  />
                </label>

                <label className="field">
                  <span>Aparece como (e-mail)</span>
                  <input
                    value={c.remetente ?? ""}
                    onChange={set("remetente")}
                    placeholder="em branco: usa o usuário acima"
                  />
                </label>
                <label className="field">
                  <span>Aparece como (nome)</span>
                  <input value={c.remetente_nome ?? ""} onChange={set("remetente_nome")} />
                </label>

                <label className="dev-check field-wide">
                  <input
                    type="checkbox"
                    checked={c.ativo !== false}
                    onChange={(e) => setDados({ ...dados, config: { ...c, ativo: e.target.checked } })}
                  />
                  Enviar lembretes por e-mail (desmarque para pausar sem apagar nada)
                </label>
              </div>

              {/* COMO O E-MAIL CHEGA: um resumo por dia, e na hora só o urgente e
                  a solicitação nova — o resto espera o resumo, para a caixa de
                  entrada não encher. */}
              <h4 className="dev-email-sub">Quando o e-mail chega</h4>
              <div className="form-grid">
                <label className="dev-check field-wide">
                  <input
                    type="checkbox"
                    checked={c.resumo_ativo !== false}
                    onChange={(e) => setDados({ ...dados, config: { ...c, resumo_ativo: e.target.checked } })}
                  />
                  Mandar um resumo por dia com tudo o que pede atenção
                </label>
                <label className="field">
                  <span>Hora do resumo</span>
                  <input
                    type="time"
                    value={c.resumo_hora ?? "08:00"}
                    onChange={set("resumo_hora")}
                    disabled={c.resumo_ativo === false}
                  />
                </label>
                <label className="dev-check field-wide">
                  <input
                    type="checkbox"
                    checked={c.urgente_ativo !== false}
                    onChange={(e) => setDados({ ...dados, config: { ...c, urgente_ativo: e.target.checked } })}
                  />
                  Avisar na hora o que é urgente (pedido de reunião parado, solicitação urgente)
                </label>
                <label className="dev-check field-wide">
                  <input
                    type="checkbox"
                    checked={c.solicitacao_ativo !== false}
                    onChange={(e) => setDados({ ...dados, config: { ...c, solicitacao_ativo: e.target.checked } })}
                  />
                  Avisar na hora cada solicitação nova que chega dos sistemas
                </label>
              </div>

              <p className="hint">
                A senha fica guardada cifrada e não volta para esta tela. Deixe o campo em branco
                para manter a que já está salva.
                {dados.testado_em ? " Último teste: " + new Date(dados.testado_em).toLocaleString("pt-BR") + "." : ""}
              </p>

              {erro && <div className="form-error">{erro}</div>}
              {aviso && <div className="dev-email-ok">{aviso}</div>}
            </>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn-secondary" onClick={onFechar} disabled={ocupado}>
            Fechar
          </button>
          <button className="btn-secondary" onClick={salvarSo} disabled={ocupado || !dados}>
            Salvar
          </button>
          <button className="btn-primary" onClick={salvarETestar} disabled={ocupado || !dados}>
            <IconMail size={15} /> {ocupado ? "Enviando…" : "Salvar e enviar teste"}
          </button>
        </div>
      </div>
    </div>
  );
}
